import { callable } from './callable'
import { listOwnerWorkspacesV2, type OwnerWorkspaceListItem } from './ownerWorkspacesService'

// R35 DL3.b: Owner Console client for the demo lane (founder-only callables, see functions/src/demoWebReleases.ts).
// Later demo-lane sections (DL4.c phone apps, DL5.b freeze) add their typed calls here.

export interface DemoWebRelease {
  releaseId: string
  gitSha: string | null
  branch: string | null
  builtAt: string | null
  hostingVersionName: string | null
  createdAt: string | null
  isLive: boolean
}

export interface DemoWebReleaseList {
  site: string
  releases: DemoWebRelease[]
  liveVersionName: string | null
  /** Set when the Hosting API could not be read; the records are still returned. */
  liveError: string | null
}

export interface DemoWebRollbackResult {
  versionName: string
  previousVersionName: string | null
  actionId: string
}

export async function listDemoWebReleases(): Promise<DemoWebReleaseList> {
  return callable<Record<string, never>, DemoWebReleaseList>('listDemoWebReleases', {})
}

export async function rollbackDemoWeb(versionName: string, reason?: string): Promise<DemoWebRollbackResult> {
  return callable<{ versionName: string; reason?: string }, DemoWebRollbackResult>('rollbackDemoWeb', { versionName, ...(reason ? { reason } : {}) })
}

// R35 DL4.c: phone update channels (founder-only callables, see functions/src/mobileChannels.ts).
export type MobileSurface = 'member' | 'helper'
export type MobileChannelName = 'sit' | 'demo'

export interface MobileRelease {
  releaseId: string
  surface: MobileSurface
  version: string
  channel: MobileChannelName | null
  flavor: string | null
  gitSha: string | null
  createdAt: string | null
  sha256: string | null
  apkUrl: string | null
}

export interface MobileChannelPointer {
  version: string | null
  releasedAt: string | null
  currentApkUrl: string | null
}

export interface MobileReleaseList {
  releases: MobileRelease[]
  pointers: Record<MobileSurface, Record<MobileChannelName, MobileChannelPointer>>
}

export interface MobileChannelSetResult {
  surface: MobileSurface
  channel: MobileChannelPointer & { name: MobileChannelName }
  previousVersion: string | null
  actionId: string
}

export async function listMobileReleases(): Promise<MobileReleaseList> {
  return callable<{ limit?: number }, MobileReleaseList>('listMobileReleases', {})
}

export async function setMobileChannelRelease(args: { surface: MobileSurface; channel: MobileChannelName; version: string }): Promise<MobileChannelSetResult> {
  return callable<{ surface: MobileSurface; channel: MobileChannelName; version: string }, MobileChannelSetResult>('setMobileChannelRelease', args)
}

// R35 DL5.b: Ready to demo (functions/src/demoReadiness.ts, founder only, read only).
export type DemoReadinessStatus = 'green' | 'amber' | 'red'
export type DemoReadinessFix = 'RESTORE_STORY' | 'GRANT_CREDITS' | 'TURN_ON_UNLOCK'

export interface DemoReadinessCheck {
  id: string
  status: DemoReadinessStatus
  detail: string
  fix?: DemoReadinessFix
}

export interface DemoReadiness {
  workspaceId: string
  checks: DemoReadinessCheck[]
  overall: DemoReadinessStatus
}

export async function getDemoReadiness(workspaceId: string, accessReason?: unknown): Promise<DemoReadiness> {
  return callable<{ workspaceId: string; accessReason?: unknown }, DemoReadiness>('getDemoReadiness', {
    workspaceId,
    ...(accessReason ? { accessReason } : {}),
  })
}

const WORKSPACE_PAGE_LIMIT = 100
const WORKSPACE_MAX_PAGES = 5

/** Workspaces with demo unlock on, read from the owner workspaces index (`demoUnlockEnabled`, DL1.d). */
export async function listDemoUnlockedWorkspaces(): Promise<OwnerWorkspaceListItem[]> {
  const found: OwnerWorkspaceListItem[] = []
  let cursor: string | null = null
  for (let page = 0; page < WORKSPACE_MAX_PAGES; page += 1) {
    const result = await listOwnerWorkspacesV2({ limit: WORKSPACE_PAGE_LIMIT, cursor })
    found.push(...result.workspaces.filter((workspace) => workspace.demoUnlockEnabled === true))
    if (!result.nextCursor) break
    cursor = result.nextCursor
  }
  return found
}
